import axios from 'axios';

// Normalize base URL: strip trailing slash and any '/api' suffix, then append '/api'
let RAW_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
RAW_BASE = RAW_BASE.replace(/\/$/, '').replace(/\/api$/, '');
const API_BASE_URL = `${RAW_BASE}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60초 (Render 무료 플랜 슬립 해제 시간 고려)
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 네트워크 오류 처리
    if (!error.response) {
      error.message = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
      return Promise.reject(error);
    }

    const status = error.response.status;

    // 콘솔 노이즈 억제: 예상 가능한 상태코드는 메시지 매핑만 하고 콘솔 출력 금지
    // 403: 접근 권한 없음(정상 차단 케이스 포함), 404: 리소스 없음(이미지 등), 409/422: 유효성 계열
    if ([403, 404, 409, 422].includes(status)) {
      if (status === 403 && !error.message) error.message = '권한이 없습니다.';
      if (status === 404 && !error.message) error.message = '요청하신 정보를 찾을 수 없습니다.';
      return Promise.reject(error);
    }

    // 서버 오류 처리
    if (status >= 500) {
      error.message = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      return Promise.reject(error);
    }

    // 401 또는 403(Invalid token) 모두 토큰 재발급 시도
    const isAuthError = error.response?.status === 401 ||
      (error.response?.status === 403 && (error.response?.data?.message || '').toLowerCase().includes('invalid token'));

    if (isAuthError && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });

          const { accessToken, refreshToken: newRefreshToken } = response.data || {};
          if (accessToken) localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (userData: {
    email: string;
    name: string;
    password: string;
    phone?: string;
    realName: string;
    churchName: string;
    phoneNumber: string;
  }) =>
    api.post('/auth/register', userData),
  
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  kakaoLogin: (code: string, redirectUri?: string) =>
    api.post('/auth/kakao', { code, redirectUri }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/me'),
  
  updateProfile: (data: {
    name?: string;
    phone?: string;
    avatar?: string;
  }) =>
    api.put('/users/me', data),
  
  deleteAccount: () => api.delete('/users/me'),
};

// Crews API
export const crewsAPI = {
  getAll: () => api.get('/crews'),
  
  getById: (id: string) => api.get(`/crews/${id}`),
  
  join: (id: string) => api.post(`/crews/${id}/join`),
  
  leave: (id: string) => api.delete(`/crews/${id}/leave`),
  
  getMyCrew: () => api.get('/crews/user/my-crews'),
};

// Posts API
export const postsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    crewId?: string;
    type?: string;
  }) =>
    api.get('/posts', { params }),
  
  create: (data: {
    content: string;
    type?: string;
    crewId?: string;
    isPublic?: boolean;
  }) =>
    api.post('/posts', data),
  
  like: (id: string) => api.post(`/posts/${id}/like`),
  
  getComments: (id: string) => api.get(`/posts/${id}/comments`),
  
  addComment: (id: string, content: string) =>
    api.post(`/posts/${id}/comments`, { content }),
};

// Prayer API
export const prayerAPI = {
  getLogs: (params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) =>
    api.get('/prayers', { params }),
  
  logPrayer: (data: {
    duration: number;
    date?: string;
  }) =>
    api.post('/prayers', data),
  
  getStats: (period?: 'week' | 'month' | 'year') =>
    api.get('/prayers/stats', { params: { period } }),
  
  getLeaderboard: (params?: {
    period?: 'week' | 'month';
    limit?: number;
  }) =>
    api.get('/prayers/leaderboard', { params }),
};

// Devotion API
export const devotionAPI = {
  getLogs: (params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) =>
    api.get('/devotions', { params }),
  
  create: (data: {
    verse: string;
    reference: string;
    content?: string;
    date?: string;
  }) =>
    api.post('/devotions', data),
  
  getTodayVerse: () => api.get('/devotions/today-verse'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }) =>
    api.get('/notifications', { params }),
  
  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`),
  
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),
  
  getUnreadCount: () => api.get('/notifications/unread-count'),
};
